import React, { Fragment } from 'react';
import { FormControl } from 'react-bootstrap';
import _ from 'lodash';
import styles from './audioInput.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faTimes } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18next';
import classnames from 'classnames/bind';
import { faFileAudio } from '@fortawesome/free-regular-svg-icons';

type AudioInputProps = {
  className?: string;
  validTypes: string[];
  url?: string;
  file?: File;
  onChange: (file: File | undefined) => void;
  errorBorderHint: boolean;
  icon: any;
};

export class AudioInput extends React.Component<AudioInputProps, any> {
  inputRef: any;
  classNames: any;
  validTypes: string[];

  static defaultProps = {
    validTypes: ['audio/mpeg'],
    icon: faFileAudio
  };

  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.classNames = classnames.bind(styles);
    this.state = {
      showAudioIcon: false,
      hovering: false,
      audioUrl: undefined
    };
    this.validTypes = props.validTypes;
  }

  componentDidMount() {
    if (this.props.file || this.props.url) {
      this.setState({
        showAudioIcon: true,
        hovering: false,
        audioUrl: this.getAudioUrl()
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.file && prevProps.file !== this.props.file) ||
      (this.props.url && prevProps.url !== this.props.url)
    ) {
      this.setState({
        showAudioIcon: true,
        hovering: false,
        audioUrl: this.getAudioUrl()
      });
    }
  }

  getAudioUrl = () => {
    return this.props.file
      ? URL.createObjectURL(this.props.file)
      : this.props.url;
  };

  onFilePreviewClick = () => {
    this.inputRef.current && this.inputRef.current.click();
  };

  onFileChange = (event) => {
    const files = _.get(event, 'currentTarget.files', []);
    const file = files[0];
    if (!file) {
      return;
    }
    this.props.onChange && this.props.onChange(file);
    event.target.value = null;
  };

  onPreviewAreaMouseEnter = () => {
    this.setState({ hovering: true });
  };

  onPreviewAreaMouseLeave = () => {
    this.setState({ hovering: false });
  };

  onClearBtnClicked = (e) => {
    if (this.inputRef.current) {
      this.inputRef.current.value = '';
    }
    this.props.onChange && this.props.onChange(undefined);
    this.setState({
      showAudioIcon: false,
      hovering: false,
      audioUrl: undefined
    });
    e.stopPropagation();
  };

  onDragOver = (event) => {
    event.dataTransfer.dropEffect = 'copy';
    event.preventDefault();
  };

  onDragEnter = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  onFileDrop = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const files = _.get(event, 'dataTransfer.files', []);
    const file = files[0];
    if (!file) {
      return;
    }

    this.props.onChange && this.props.onChange(file);
    if (this.validTypes.indexOf(file.type) === -1) {
      this.setState({
        showAudioIcon: false,
        hovering: false,
        audioUrl: undefined
      });
    }
  };

  renderPlaceholder = () => {
    return (
      <div
        className={styles.filePlaceholder}
        onDragOver={this.onDragOver}
        onDragEnter={this.onDragEnter}
        onDrop={this.onFileDrop}
      >
        <FontAwesomeIcon icon={faFileUpload} />
        <div className={styles.hint}>
          {i18n.t('creativeSetupFlow.labels.dragdropHint')}
        </div>
      </div>
    );
  };

  renderAudioPreview = () => {
    return (
      <div
        className={styles.previewArea}
        onMouseEnter={this.onPreviewAreaMouseEnter}
        onMouseLeave={this.onPreviewAreaMouseLeave}
        onDragOver={this.onDragOver}
        onDragEnter={this.onDragEnter}
        onDrop={this.onFileDrop}
      >
        {this.state.hovering && (
          <Fragment>
            <div className={styles.mask} />
            {this.renderPlaceholder()}
            <div
              className={styles.closeBtnContainer}
              onClick={this.onClearBtnClicked}
            >
              <FontAwesomeIcon className={styles.closeBtn} icon={faTimes} />
            </div>
          </Fragment>
        )}
        <div className={styles.fileIcon}>
          <FontAwesomeIcon icon={this.props.icon} />
        </div>
      </div>
    );
  };

  render() {
    const pickProps = _.pick(this.props, ['disabled', 'name']);
    const classNames = this.classNames(
      'audioInputContainer',
      this.props.className,
      {
        showErrorBorder: this.props.errorBorderHint
      }
    );
    const accept = this.props.validTypes.join(',');
    return (
      <div className={classNames}>
        <FormControl
          accept={accept}
          className={styles.fileInput}
          type='file'
          ref={this.inputRef}
          {...pickProps}
          onChange={this.onFileChange}
        />
        <div className={styles.filePreview} onClick={this.onFilePreviewClick}>
          {this.state.showAudioIcon
            ? this.renderAudioPreview()
            : this.renderPlaceholder()}
        </div>
      </div>
    );
  }
}
